import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { UploadSimple, FileArchive } from "@phosphor-icons/react";
import { UntopicedEvidenceCard } from "../components/UntopicedEvidenceCard";
import BulkBadgePhotos from "../components/BulkBadgePhotos";

const DOC_TYPES = [
  ["badge_photo", "Badge photo"], ["id_card", "ID card / driver's license"], ["ss_card", "Social Security card"],
  ["credential", "Credential / license"], ["pa_bg", "PA background check"], ["fbi_bg", "FBI background"],
  ["tb_test", "TB Testing (medical test)"], ["cpr_first_aid", "CPR / First Aid"], ["drug_test", "Drug test"],
  ["auto_insurance", "Auto insurance"], ["direct_observation", "Direct observation"],
  ["resume", "Resume"], ["other", "Other document"],
];
const RECORD_KINDS = [["competency_exam", "Competency exam"], ["interview", "Interview"], ["reference_check", "Reference check"]];

const CATEGORIES = [
  { key: "doc", group: "Single employee", label: "Employee document (ID, badge photo, credential…)", accept: ".pdf,.png,.jpg,.jpeg,.webp,.heic" },
  { key: "rec", group: "Single employee", label: "Employment record (competency exam, interview, reference)", accept: ".pdf,.png,.jpg,.jpeg,.webp" },
  { key: "ev", group: "Single employee", label: "Training evidence (signed PDF for a topic)", accept: ".pdf" },
  { key: "tb", group: "Single employee", label: "TB Screening form (orientation + yearly)", accept: ".pdf,.png,.jpg,.jpeg,.webp" },
  { key: "visits", group: "Bulk", label: "BULK — Visit log / EVV visits (HHAeXchange Excel/CSV, up to ~80k rows)", accept: ".xlsx,.xls,.csv,.tsv,.txt", special: "visits" },
  { key: "ev-zip", group: "Bulk", label: "BULK — Training proof (ZIP of PDFs — filename: Training Title - Person Name - Date; TB Screening and TB Training are separate PDFs)", accept: ".zip", topic: true, special: "evzip" },
  { key: "comp-zip", group: "Bulk", label: "BULK — Competency exams (ZIP or PDF)", accept: ".zip,.pdf", url: "/competency/import-zip",
    fmt: (d) => `Attached ${d.created ?? d.imported ?? 0} · already on file ${d.skipped ?? 0}${(d.unmatched || d.results?.filter((r) => r.status === "unmatched") || []).length ? ` — unmatched: ${(d.unmatched || d.results?.filter((r) => r.status === "unmatched").map((r) => r.file || r)).slice(0, 8).join(", ")}` : ""}` },
  { key: "tb-bulk", group: "Bulk", label: "BULK — TB Screening forms (ZIP or several files)", accept: ".zip,.pdf,.png,.jpg,.jpeg,.webp", multiple: true, url: "/tb-screen/upload",
    fmt: (d) => `Attached ${d.attached ?? 0}${d.replaced ? ` (${d.replaced} updated — previous version kept in history)` : ""}${d.unmatched?.length ? ` — no staff match in filename (saved to the document inbox below): ${d.unmatched.slice(0, 6).join(", ")}${d.unmatched.length > 6 ? ` +${d.unmatched.length - 6} more` : ""}` : ""}` },
  { key: "interviews", group: "Bulk", label: "BULK — Interview records (CSV/Excel)", accept: ".csv,.xlsx,.xls", url: "/interviews/import",
    fmt: (d) => `Created ${d.created ?? 0} · already existed ${d.existing ?? d.skipped ?? 0}${d.unmatched?.length ? ` — unmatched: ${d.unmatched.slice(0, 8).join(", ")}` : ""}` },
  { key: "skills", group: "Bulk", label: "BULK — Skills & experience survey (CSV)", accept: ".csv", url: "/skills/import",
    fmt: (d) => `Updated ${d.matched ?? 0} profile(s)${d.unmatched?.length ? ` — unmatched: ${d.unmatched.slice(0, 8).join(", ")}` : ""}` },
  { key: "applications", group: "Bulk", label: "BULK — Employment applications (Excel/CSV)", accept: ".xlsx,.csv", url: "/applications/import",
    fmt: (d) => `Attached to ${d.matched ?? 0} record(s)${d.unmatched?.length ? ` — skipped (not on roster): ${d.unmatched.slice(0, 8).join(", ")}${d.unmatched.length > 8 ? ` +${d.unmatched.length - 8} more` : ""}` : ""}` },
  { key: "employees", group: "Bulk", label: "BULK — Employee roster (CSV/Excel)", accept: ".csv,.xlsx,.xls", url: "/import/employees-csv",
    fmt: (d) => `Created ${d.created ?? 0} · already existed ${d.existing ?? d.skipped ?? 0}${d.temp_passwords?.length ? " — copy the temporary passwords shown before closing" : ""}` },
  { key: "clients", group: "Bulk", label: "BULK — Client list (CSV/Excel)", accept: ".csv,.xlsx,.xls", url: "/import/clients-csv",
    fmt: (d) => `Created ${d.created ?? 0} · skipped ${d.skipped ?? d.existing ?? 0}` },
  { key: "evv-month", group: "Bulk", label: "BULK — EVV compliance numbers (CSV, per month)", accept: ".csv,.xlsx,.xls", url: "/evv/upload", month: true,
    fmt: (d) => `Updated ${d.updated ?? d.matched ?? 0} employee record(s)${d.unmatched?.length ? ` — unmatched: ${d.unmatched.slice(0, 8).join(", ")}` : ""}` },
  { key: "mp", group: "Bulk", label: "BULK — Missed punch forms (signed PDFs)", accept: ".pdf", multiple: true, url: "/missed-punch/upload",
    fmt: (d) => `Uploaded ${d.uploaded ?? d.count ?? (d.files || []).length} form(s)${d.unmatched?.length ? ` — unmatched: ${d.unmatched.slice(0, 8).join(", ")}` : ""}` },
  { key: "namekey", group: "Bulk", label: "BULK — Name key mapping (masked → real names, Excel/CSV)", accept: ".xlsx,.xls,.csv,.tsv,.txt", url: "/name-key/import",
    fmt: (d) => `Imported ${d.imported ?? d.mappings ?? d.count ?? 0} mapping(s)` },
  { key: "completions", group: "Bulk", label: "BULK — Training completions seed (CSV/Excel: employee, training, date)", accept: ".csv,.xlsx,.xls", url: "/training/import-completions-csv",
    fmt: (d) => `Recorded ${d.recorded ?? 0} completion(s) · ${d.queued ?? 0} queued for review · ${d.skipped_pending ?? 0} pending rows ignored · ${d.skipped_duplicates ?? 0} already on file` },
];
const SINGLE_KEYS = ["doc", "rec", "ev", "tb"];

function DocumentInboxCard({ staff }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [scanning, setScanning] = useState("");
  const [sel, setSel] = useState({});
  const [previews, setPreviews] = useState({});

  const load = () => api.get("/document-inbox").then((r) => setItems(r.data)).catch((e) => { toast.error(formatApiError(e)); setItems({ pending: [], assigned: [] }); });
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!items) return;
    items.pending.filter((d) => (d.content_type || "").startsWith("image/") && !previews[d.id]).forEach((d) => {
      api.get(`/document-inbox/${d.id}/file`, { responseType: "blob" })
        .then((r) => setPreviews((p) => ({ ...p, [d.id]: URL.createObjectURL(r.data) })))
        .catch(() => {});
    });
  }, [items]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    let done = 0;
    let failed = 0;
    try {
      const CHUNK = 8;
      for (let i = 0; i < files.length; i += CHUNK) {
        const batch = files.slice(i, i + CHUNK);
        const fd = new FormData();
        for (const f of batch) fd.append("files", f);
        try {
          await api.post("/document-inbox", fd, { timeout: 120000 });
          done += batch.length;
          setProgress(`Uploading… ${Math.min(i + CHUNK, files.length)}/${files.length}`);
        } catch (e) {
          failed += batch.length;
          toast.error(`Batch failed (${batch.length} files): ${formatApiError(e)}`);
        }
      }
      toast.success(`${done} file(s) added to the inbox${failed ? ` — ${failed} failed, retry those` : ""}`);
      window.dispatchEvent(new Event("uploads-changed"));
      load();
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  const scan = async (iid) => {
    setScanning(iid);
    try {
      const { data } = await api.post(`/document-inbox/${iid}/scan`, {}, { timeout: 120000 });
      const name = data.extracted?.name || "";
      toast.success(name ? `Read it — looks like ${name}` : "Scanned — no name readable");
      if (name) {
        const toks = name.toLowerCase().split(/\s+/).filter(Boolean);
        const guess = staff.find((u) => (u.name || "").toLowerCase() === name.toLowerCase())
          || staff.find((u) => toks.length > 1 && toks.every((t) => (u.name || "").toLowerCase().includes(t)));
        if (guess) {
          setSel((s) => ({ ...s, [iid]: { ...(s[iid] || {}), uid: guess.id } }));
          toast.success(`Pre-picked ${guess.name} — confirm below`);
        }
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setScanning("");
  };

  const scanAll = async () => {
    try {
      await api.post("/document-inbox/scan-all");
      toast.success("AI is reading every file — suggestions appear as it goes (nothing is assigned automatically)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (items?.scan_job?.status !== "running") return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [items?.scan_job?.status]); // eslint-disable-line

  const setField = (iid, k, v) => setSel((s) => ({ ...s, [iid]: { ...(s[iid] || {}), [k]: v } }));

  const [picks, setPicks] = useState({});
  const isPicked = (iid, f) => picks[iid]?.[f] !== false;
  const togglePick = (iid, f) => setPicks((p) => ({ ...p, [iid]: { ...(p[iid] || {}), [f]: picks[iid]?.[f] === false } }));
  const isExpiredDate = (iso) => !!iso && String(iso).slice(0, 10) < new Date().toISOString().slice(0, 10);

  const assign = async (iid) => {
    const item = pending.find((x) => x.id === iid) || {};
    const s = sel[iid] || {};
    const uid = s.uid || item.suggested_uid || "";
    const doctype = s.doctype || item.suggested_doctype || "";
    if (!uid || !doctype) { toast.error("Pick the employee and the document type"); return; }
    const meta = {};
    if (doctype === "id_card") {
      meta.id_type = s.id_type || "";
      if (isPicked(iid, "id_number")) meta.id_number = s.id_number || item.extracted?.id_number || "";
      if (isPicked(iid, "state")) meta.id_state = s.id_state || item.extracted?.state || "";
      if (isPicked(iid, "issued")) meta.issue = s.issue || item.extracted?.issued || "";
      if (isPicked(iid, "expires")) meta.expiry = s.expiry || item.extracted?.expires || "";
    }
    if (doctype === "tb_test") {
      meta.tb_test_type = s.tb_test_type || "";
      meta.step1 = s.step1 || "";
      meta.step2 = s.step2 || "";
    }
    setBusy(true);
    try {
      const payload = { user_id: uid, doc_type: doctype, completed_date: s.date || "", meta, apply_dob: isPicked(iid, "dob") };
      if (uid === "__create__") {
        const nm = window.prompt("Name for the history record (former employee — gets a record, not a login):", item.suggested_name || item.extracted?.name || "");
        if (!nm) { setBusy(false); return; }
        payload.record_name = nm;
      }
      const { data } = await api.post(`/document-inbox/${iid}/assign`, payload);
      toast.success(data.created_record ? `Created history record for ${data.assigned_to} and attached` : "Attached to the employee's record");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const discard = async (iid) => {
    if (!window.confirm("Discard this inbox item?")) return;
    try { await api.delete(`/document-inbox/${iid}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const pending = items?.pending || [];
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="inbox-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-heading text-sm font-semibold text-stone-900">Document inbox <span className="text-xs font-normal text-stone-400">— snap a photo on your phone, assign it later</span></p>
        <p className="mt-0.5 text-[11px] text-stone-400">Licenses &amp; SS cards are quarantined here until you merge: assigning one checks name/DOB/address against the application and profile (conflicts show on the employee file), and fills DOB if the profile has none.</p>          <p className="mt-0.5 max-w-2xl text-xs text-stone-400">Upload ID / SS card / certificate photos here. Tap "Read it" and the app reads the name, numbers, and dates off the photo. Nothing lands on an employee's record until you pick them and attach.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(progress || items?.scan_job?.status === "running") && (
            <span className="text-xs font-medium text-primary" data-testid="inbox-progress">
              {progress || `AI reading… ${items?.scan_job?.done ?? 0}/${items?.scan_job?.total ?? 0}`}
            </span>
          )}
          {pending.length > 0 && pending.some((d) => !d.extracted) && (
            <button onClick={scanAll} disabled={items?.scan_job?.status === "running"}
              className="inline-flex h-9 items-center gap-1.5 rounded-full border border-primary/40 px-4 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-white disabled:opacity-50" data-testid="inbox-scan-all">
              {items?.scan_job?.status === "running" ? "Reading…" : "Read all with AI"}
            </button>
          )}
          <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-stone-900" data-testid="inbox-upload-label"><UploadSimple size={15} /> {busy ? "Uploading…" : "Add photos / PDFs"}
            <input type="file" accept="image/*,.pdf" multiple className="hidden" data-testid="inbox-upload-input"
              onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} />
          </label>
        </div>
      </div>

      {items === null ? (
        <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : pending.length === 0 ? (
        <p className="mt-4 rounded-md border border-dashed border-stone-200 py-6 text-center text-xs text-stone-400" data-testid="inbox-empty">Inbox is empty — photos you add wait here until you assign them.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {pending.map((d) => (
            <div key={d.id} className="rounded-lg border border-stone-200 p-3" data-testid={`inbox-item-${d.id}`}>
              <div className="flex flex-wrap items-start gap-3">
                {previews[d.id] ? (
                  <img src={previews[d.id]} alt={d.filename} className="h-16 w-16 rounded-md border border-stone-200 object-cover" data-testid={`inbox-thumb-${d.id}`} />
                ) : (
                  <span className="flex h-16 w-16 items-center justify-center rounded-md border border-stone-200 bg-stone-50 text-[10px] text-stone-400">{d.content_type === "application/pdf" ? "PDF" : "…"}</span>
                )}
                <div className="min-w-40 flex-1">
                  <p className="text-sm font-medium text-stone-800">{d.filename}</p>
                  <p className="text-[10px] text-stone-400">added {String(d.uploaded_at || "").slice(0, 16).replace("T", " ")} by {d.uploaded_by}</p>
                  {d.suggested_name && (
                    <p className="mt-0.5 text-[11px] font-medium text-primary" data-testid={`inbox-suggest-${d.id}`}>
                      AI thinks: {d.suggested_name}{d.suggested_doctype ? ` · ${d.suggested_doctype.replace(/_/g, " ")}` : ""} — confirm below
                    </p>
                  )}
                  {d.extracted && Object.keys(d.extracted).length > 0 && (
                    <div className="mt-1" data-testid={`inbox-extracted-${d.id}`}>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Read from the document — click a field to include/exclude it when you attach:</p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {d.extracted.document_guess && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">{d.extracted.document_guess.replace(/_/g, " ")}</span>}
                        {d.extracted.name && <span className="rounded-full border border-stone-200 px-2 py-0.5 text-[10px] font-semibold text-stone-700">{d.extracted.name}</span>}
                        {[["id_number", `ID # ${d.extracted.id_number}`], ["state", `State ${d.extracted.state}`], ["dob", `DOB ${d.extracted.dob}`], ["issued", `Issued ${d.extracted.issued}`], ["expires", `Expires ${d.extracted.expires}`], ["address", d.extracted.address]]
                          .filter(([f]) => d.extracted[f])
                          .map(([f, label]) => (
                            <button key={f} type="button" onClick={() => togglePick(d.id, f)}
                              title={isPicked(d.id, f) ? "Will populate the employee record — click to exclude" : "Excluded — click to include"}
                              className={`rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${
                                f === "expires" && isExpiredDate(d.extracted.expires)
                                  ? isPicked(d.id, f) ? "border-[#C0392B] bg-[#C0392B] text-white" : "border-stone-300 text-stone-400 line-through"
                                  : isPicked(d.id, f) ? "border-primary/50 bg-primary/10 text-primary" : "border-stone-300 text-stone-400 line-through"}`}
                              data-testid={`inbox-pick-${f}-${d.id}`}>
                              {label}{f === "expires" && isExpiredDate(d.extracted.expires) ? " — EXPIRED" : ""}
                            </button>
                          ))}
                      </div>
                      {isExpiredDate(d.extracted.expires) && isPicked(d.id, "expires") && (
                        <p className="mt-1 text-[11px] font-semibold text-[#C0392B]" data-testid={`inbox-expired-warn-${d.id}`}>This ID is expired — attaching it sets the expiration on file and it will flag in compliance until renewed.</p>
                      )}
                    </div>
                  )}
                  {d.ocr_text && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-[11px] font-medium text-primary" data-testid={`inbox-ocr-toggle-${d.id}`}>Read text</summary>
                      <pre className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md bg-stone-50 p-2 text-[11px] text-stone-600" data-testid={`inbox-ocr-${d.id}`}>{d.ocr_text}</pre>
                    </details>
                  )}
                </div>
                <button onClick={() => scan(d.id)} disabled={scanning === d.id}
                  className="rounded-full border border-primary/40 px-3 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary hover:text-white disabled:opacity-50"
                  data-testid={`inbox-scan-${d.id}`}>
                  {scanning === d.id ? "Reading…" : d.ocr_text || d.extracted ? "Re-read" : "Read it"}
                </button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-stone-100 pt-3">
                <select value={sel[d.id]?.uid || d.suggested_uid || ""} onChange={(e) => setField(d.id, "uid", e.target.value)}
                  className="h-8 max-w-48 rounded-md border border-stone-300 bg-white px-1.5 text-xs" data-testid={`inbox-employee-${d.id}`}>
                  <option value="">Assign to employee…</option>
                  <option value="__create__">＋ History record (former employee — no login)</option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select value={sel[d.id]?.doctype || d.suggested_doctype || ""} onChange={(e) => setField(d.id, "doctype", e.target.value)}
                  className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs" data-testid={`inbox-doctype-${d.id}`}>
                  <option value="">Document type…</option>
                  {DOC_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input type="date" value={sel[d.id]?.date || ""} onChange={(e) => setField(d.id, "date", e.target.value)}
                  title={["pa_bg", "fbi_bg"].includes(sel[d.id]?.doctype) ? "Date the background check was run/received" : "Date on the document (optional)"} className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-600" data-testid={`inbox-date-${d.id}`} />
                {(sel[d.id]?.doctype || d.suggested_doctype) === "id_card" && (
                  <>
                    <select value={sel[d.id]?.id_type || ""} onChange={(e) => setField(d.id, "id_type", e.target.value)}
                      className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs" data-testid={`inbox-idtype-${d.id}`}>
                      <option value="">ID type…</option>
                      <option>PA driver's license</option><option>PA photo ID</option>
                      <option>US passport</option><option>Other state DL / ID</option>
                    </select>
                    <input placeholder={d.extracted?.id_number ? `License / ID # (read: ${d.extracted.id_number})` : "License / ID number"}
                      value={sel[d.id]?.id_number || ""} onChange={(e) => setField(d.id, "id_number", e.target.value)}
                      className="h-8 w-32 rounded-md border border-stone-300 bg-white px-1.5 text-xs" data-testid={`inbox-idnum-${d.id}`} />
                    <input placeholder="State" maxLength={2}
                      value={sel[d.id]?.id_state || ""} onChange={(e) => setField(d.id, "id_state", e.target.value.toUpperCase())}
                      className="h-8 w-14 rounded-md border border-stone-300 bg-white px-1.5 text-xs" data-testid={`inbox-idstate-${d.id}`} />
                    <input type="date" title={d.extracted?.issued ? `Issue date (read: ${d.extracted.issued})` : "Issue date"}
                      value={sel[d.id]?.issue || ""} onChange={(e) => setField(d.id, "issue", e.target.value)}
                      className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-600" data-testid={`inbox-idissue-${d.id}`} />
                    <input type="date" title={d.extracted?.expires ? `Expiration (read: ${d.extracted.expires})` : "Expiration date"}
                      value={sel[d.id]?.expiry || ""} onChange={(e) => setField(d.id, "expiry", e.target.value)}
                      className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-600" data-testid={`inbox-idexpiry-${d.id}`} />
                  </>
                )}
                {(sel[d.id]?.doctype || d.suggested_doctype) === "tb_test" && (
                  <>
                    <select value={sel[d.id]?.tb_test_type || ""} onChange={(e) => setField(d.id, "tb_test_type", e.target.value)}
                      className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs" data-testid={`inbox-tbtype-${d.id}`}>
                      <option value="">TB type…</option>
                      <option>2-step skin test (TST)</option><option>TB Gold blood test</option><option>Chest x-ray</option>
                    </select>
                    {(sel[d.id]?.tb_test_type || "").startsWith("2-step") && (
                      <>
                        <input type="date" title="Step 1 date — work may begin after this"
                          value={sel[d.id]?.step1 || ""} onChange={(e) => setField(d.id, "step1", e.target.value)}
                          className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-600" data-testid={`inbox-tbstep1-${d.id}`} />
                        <input type="date" title="Step 2 date — completes the 2-step"
                          value={sel[d.id]?.step2 || ""} onChange={(e) => setField(d.id, "step2", e.target.value)}
                          className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs text-stone-600" data-testid={`inbox-tbstep2-${d.id}`} />
                      </>
                    )}
                  </>
                )}
                {!(sel[d.id]?.uid || d.suggested_uid) || !(sel[d.id]?.doctype || d.suggested_doctype) ? (
                  <span className="text-[11px] font-medium text-[#8a5d14]" data-testid={`inbox-attach-hint-${d.id}`}>
                    ↑ Pick a person and a document type to attach
                  </span>
                ) : null}
                <Button size="sm" onClick={() => assign(d.id)} disabled={busy || !(sel[d.id]?.uid || d.suggested_uid) || !(sel[d.id]?.doctype || d.suggested_doctype)}
                  className="h-9 rounded-full bg-primary px-5 text-xs font-semibold text-primary-foreground hover:bg-stone-900 disabled:opacity-40" data-testid={`inbox-attach-${d.id}`}>Attach to their record</Button>
                <button onClick={() => discard(d.id)} className="ml-auto text-stone-300 hover:text-[#C0392B]" title="Discard" data-testid={`inbox-discard-${d.id}`}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(items?.assigned || []).length > 0 && (
        <div className="mt-4 border-t border-stone-100 pt-3" data-testid="inbox-assigned">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Recently assigned</p>
          <ul className="mt-1 space-y-0.5 text-xs text-stone-500">
            {items.assigned.slice(0, 5).map((d) => (
              <li key={d.id} data-testid={`inbox-assigned-${d.id}`}>{d.filename} → {d.assigned_name} ({(DOC_TYPES.find(([k]) => k === d.doc_type) || [, d.doc_type])[1]})</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function UploadLogCard() {
  const [rows, setRows] = useState(null);
  const [openRow, setOpenRow] = useState("");
  const load = useCallback(() => { api.get("/admin/upload-log").then((r) => setRows(r.data?.rows || [])).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("uploads-changed", h);
    return () => window.removeEventListener("uploads-changed", h);
  }, [load]);
  if (rows === null || rows.length === 0) return null;
  const KIND_LABELS = { "training-import": "Training import", "evidence-import": "Evidence import", "inbox-merge": "Inbox merge", "inbox-discard": "Inbox discard", "badge": "Badge", "id-check": "ID check" };
  const OUTCOME_CLS = { recorded: "text-[#1e8449]", skipped: "text-[#C0392B]", queued: "text-[#8a5d14]", unmatched: "text-[#8a5d14]" };
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="upload-log-card">
      <p className="font-heading text-sm font-semibold text-stone-900">Upload &amp; import log <span className="text-xs font-normal text-stone-400">— what was done, row by row (kept for the record)</span></p>
      <div className="mt-3 max-h-72 space-y-1.5 overflow-y-auto">
        {rows.slice(0, 40).map((r) => (
          <div key={r.id} className="text-xs" data-testid={`upload-log-${r.id}`}>
            <button type="button" onClick={() => setOpenRow(openRow === r.id ? "" : r.id)} className="flex w-full items-start gap-2 text-left" data-testid={`upload-log-toggle-${r.id}`}>
              <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${r.status === "ok" ? "bg-[#27AE60]" : r.status === "warn" ? "bg-[#B7791F]" : "bg-[#C0392B]"}`} />
              <div className="min-w-0 flex-1">
                <p className="text-stone-700">
                  {KIND_LABELS[r.kind] && <span className="mr-1.5 rounded-full bg-stone-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-stone-500">{KIND_LABELS[r.kind]}</span>}
                  {r.file_count != null && <>{r.file_count} file(s){r.total_bytes ? ` · ${(r.total_bytes / 1048576).toFixed(1)} MB` : ""} · </>}
                  <span className="text-stone-400">{String(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  {r.rows?.length > 0 && <span className="ml-1.5 text-primary">{openRow === r.id ? "▾ hide rows" : `▸ ${r.row_count || r.rows.length} rows`}</span>}
                </p>
                {r.detail && <p className="text-stone-500">{r.detail}</p>}
                {r.status !== "ok" && (r.filenames || []).length > 0 && (
                  <p className="text-[#C0392B]">{(r.filenames || []).slice(0, 10).join(", ")}{(r.filenames || []).length > 10 ? ` +${r.filenames.length - 10} more` : ""}</p>
                )}
              </div>
            </button>
            {openRow === r.id && r.rows?.length > 0 && (
              <ul className="ml-4 mt-1 max-h-48 space-y-0.5 overflow-y-auto rounded-md border border-stone-100 bg-stone-50 p-2" data-testid={`upload-log-rows-${r.id}`}>
                {r.rows.map((row, i) => (
                  <li key={i} className="text-[11px]">
                    <span className={`font-semibold ${OUTCOME_CLS[row.outcome] || "text-stone-500"}`}>{row.outcome}</span>{" "}
                    <span className="text-stone-700">{row.person}</span>
                    {row.topic ? <span className="text-stone-500"> · {row.topic}</span> : null}
                    {row.date ? <span className="text-stone-500"> · {row.date}</span> : null}
                    {row.reason ? <span className="text-stone-400"> — {row.reason}</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ClearTrainingCard() {
  const [counts, setCounts] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const loadCounts = () => api.get("/admin/training-data/counts").then((r) => setCounts(r.data)).catch(() => {});
  useEffect(() => { loadCounts(); }, []);
  useEffect(() => {
    const h = () => loadCounts();
    window.addEventListener("uploads-changed", h);
    return () => window.removeEventListener("uploads-changed", h);
  }, []);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/training-data/clear", { confirm });
      toast.success(`Cleared ${data.cleared.course_completions ?? 0} dated completions and ${data.cleared.evidence_records} proof files — visits untouched`);
      setConfirm("");
      loadCounts();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-[#C0392B]/30 bg-white p-5 shadow-sm" data-testid="clear-training-card">
      <p className="font-heading text-sm font-semibold text-stone-900">Clear all training data</p>
      <p className="mt-0.5 text-xs text-stone-400">Wipes ALL of training — every employee's training records AND the training catalog itself (all topics, quizzes, packs, videos) — so training can be rebuilt from scratch. Staff, clients, visits (EVV), and non-training documents are NOT touched. Rebuild the trainings before uploading the training spreadsheet, or rows will have nothing to match.</p>
      {counts && (
        <p className="mt-2 text-xs text-stone-600" data-testid="clear-training-counts">
          Right now: {counts.course_completions ?? 0} dated completions · {counts.evidence_records} proof files · {counts.users_with_history} staff with history · {counts.queued_imports} queued imports · {counts.topics} trainings · {counts.packs} packs · {counts.videos} videos
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type CLEAR TRAINING DATA"
          className="h-9 w-56 rounded-md border border-stone-300 px-3 text-xs" data-testid="clear-training-confirm" />
        <Button onClick={run} disabled={busy || confirm !== "CLEAR TRAINING DATA"} variant="outline"
          className="h-9 rounded-full border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B] hover:text-white" data-testid="clear-training-button">
          {busy ? "Clearing…" : "Clear training data"}
        </Button>
      </div>
    </div>
  );
}

const RECURRING_FILES = [
  { key: "visit_log", label: "Visit log — all visits", desc: "The full HHAeXchange visit export. Uses the preview-first visit importer below.", shortcut: "visits" },
  { key: "evv_compliance", label: "EVV compliance by employee", desc: "The per-caregiver EVV compliance report. You'll pick the month below.", shortcut: "evv-month" },
  { key: "payroll_details", label: "Payroll details", desc: "Payroll export — every upload is stored so pay data stays current. CSV or Excel." },
  { key: "billing_details", label: "Billing detail", desc: "Billing export — every upload is stored so billing data stays current. CSV or Excel." },
];

function RecurringDataCard({ onShortcut }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/data-uploads").then((r) => setData(r.data)).catch(() => setData({ latest: {}, history: [] })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("uploads-changed", h);
    return () => window.removeEventListener("uploads-changed", h);
  }, [load]);

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post(`/data-uploads/${kind}`, fd, { timeout: 300000 });
      toast.success(`${res.filename} stored — ${res.rows} row(s) detected`);
      load();
      window.dispatchEvent(new Event("uploads-changed"));
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="recurring-data-card">
      <p className="font-heading text-sm font-semibold text-stone-900">Recurring data files <span className="text-xs font-normal text-stone-400">— upload these regularly to keep data fresh</span></p>
      <div className="mt-4 space-y-3">
        {RECURRING_FILES.map((f) => {
          const last = data?.latest?.[f.key];
          return (
            <div key={f.key} className="flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 p-4" data-testid={`recurring-${f.key}`}>
              <div className="min-w-52 flex-1">
                <p className="text-sm font-semibold text-stone-900">{f.label}</p>
                <p className="mt-0.5 text-xs text-stone-500">{f.desc}</p>
                <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`recurring-last-${f.key}`}>
                  {last ? (
                    <>
                      <span className="text-stone-500">Last upload: <span className="font-medium text-stone-700">{String(last.uploaded_at || "").slice(0, 16).replace("T", " ")}</span>
                        {last.uploaded_by ? ` by ${last.uploaded_by}` : ""}{last.filename ? ` · ${last.filename}` : ""}
                        {last.rows != null ? ` · ${last.rows} row(s)` : ""}{last.detail ? ` — ${last.detail}` : ""}</span>
                      {(() => {
                        const d = last.uploaded_at ? Math.floor((Date.now() - new Date(last.uploaded_at).getTime()) / 86400000) : null;
                        return d != null && (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${d <= 7 ? "bg-[#27AE60]/10 text-[#1e8449]" : d <= 30 ? "bg-[#B7791F]/10 text-[#8a5d14]" : "bg-[#C0392B]/10 text-[#C0392B]"}`}
                            data-testid={`recurring-days-${f.key}`}>
                            {d === 0 ? "uploaded today" : `${d} day${d === 1 ? "" : "s"} ago`}
                          </span>
                        );
                      })()}
                    </>
                  ) : (
                    <span className="font-medium text-[#8a5d14]">Never uploaded yet</span>
                  )}
                </p>
              </div>
              {f.shortcut ? (
                <Button variant="outline" onClick={() => onShortcut(f.shortcut)}
                  className="border-primary/40 text-primary hover:bg-primary hover:text-white" data-testid={`recurring-upload-${f.key}`}>
                  <UploadSimple size={15} className="mr-1.5" /> Upload
                </Button>
              ) : (
                <label className={`inline-flex h-9 items-center gap-1.5 rounded-md border border-primary/40 px-3 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-white ${busy ? "opacity-50" : "cursor-pointer"}`} data-testid={`recurring-upload-${f.key}`}>
                  <UploadSimple size={15} /> {busy === f.key ? "Uploading…" : "Upload"}
                  <input type="file" accept=".csv,.xlsx,.xls,.tsv,.txt,.pdf" className="hidden" disabled={!!busy}
                    onChange={(e) => { upload(f.key, e.target.files?.[0]); e.target.value = ""; }} data-testid={`recurring-input-${f.key}`} />
                </label>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmployeePackImport() {
  const [packFile, setPackFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dateJob, setDateJob] = useState(null);

  const backfillDates = async () => {
    setDateJob({ done: 0, filled: 0, remaining: null, finished: false });
    try {
      let processed = 0, totalFilled = 0;
      for (;;) {
        const { data } = await api.post("/employees/backfill-doc-dates", { limit: 10 }, { timeout: 600000 });
        processed += data.processed;
        totalFilled += data.filled;
        setDateJob({ done: processed, filled: totalFilled, remaining: data.remaining, finished: data.remaining === 0 });
        if (data.remaining === 0) {
          const { data: prov } = await api.post("/employees/backfill-provenance", {}, { timeout: 300000 });
          toast.success(`Done — filled ${totalFilled} document date(s), updated ${data.profiles || 0} employee record(s), stamped sources on ${prov.profiles || 0} record(s), archived ${prov.archived_tasks || 0} old audit note(s)`);
          break;
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setDateJob(null);
    }
  };

  const uploadAndPreview = async () => {
    if (!packFile) { toast.error("Choose the ZIP first"); return; }
    setBusy(true);
    setManifest(null);
    setResult(null);
    try {
      const CHUNK = 4 * 1024 * 1024;
      const { data: start } = await api.post("/employee-packs/import/start", {});
      const total = Math.max(1, Math.ceil(packFile.size / CHUNK));
      for (let i = 0; i < total; i++) {
        await api.post(`/employee-packs/import/chunk/${start.upload_id}`, packFile.slice(i * CHUNK, (i + 1) * CHUNK), {
          headers: { "Content-Type": "application/octet-stream" }, timeout: 120000,
        });
        setProgress({ done: i + 1, total });
      }
      const { data } = await api.post("/employee-packs/import/dry-run",
        { upload_id: start.upload_id, filename: packFile.name }, { timeout: 600000 });
      setManifest({ ...data, upload_id: start.upload_id });
      toast.success(`Preview ready — ${data.employees.length} employee pack(s), ${data.total_documents} documents. Nothing imported yet.`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
    setProgress(null);
  };

  const runImport = async (attempt = 0) => {
    if (!manifest) return;
    setBusy(true);
    try {
      const { data } = await api.post("/employee-packs/import/run",
        { upload_id: manifest.upload_id, filename: packFile?.name || "pack.zip" }, { timeout: 600000 });
      setResult(data);
      setManifest(null);
      setPackFile(null);
      const created = data.results.filter((r) => r.created_user).length;
      const trainings = data.results.reduce((n, r) => n + (r.trainings_recorded || 0) + (r.competency_records || 0), 0);
      toast.success(`Import complete — ${data.results.length} employee file(s), ${data.results.reduce((n, r) => n + r.imported, 0)} documents filed${created ? `, ${created} new staff record(s) created` : ""}${trainings ? `, ${trainings} training/competency records` : ""}`);
      window.dispatchEvent(new Event("uploads-changed"));
    } catch (e) {
      // Long imports can outlive the proxy while the server keeps working — re-running
      // is safe (imported docs/tasks/history are skipped) and picks up where it left off.
      if (attempt < 2) {
        toast.info("Still working — continuing the import…");
        setBusy(false);
        return runImport(attempt + 1);
      }
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="employee-pack-import">
      <p className="font-heading text-sm font-semibold text-stone-900">Employee files — bulk audit-pack import</p>
      <p className="mt-1 text-xs text-stone-500">
        One big ZIP holding many employee packs (a folder, ZIP, or combined audit-book PDF per employee, named like <code className="rounded bg-accent px-1">First_Last_100115_Employee_File</code> or <code className="rounded bg-accent px-1">Silver_Home_Care_First_Last_100299_Auditor_Book.pdf</code>) — or a single combined audit-book PDF on its own.
        Locked auditor books are split into individual artifacts using the BOOK INDEX on page 1. Group manifest/reconciliation files are ignored.
        Every document is pulled out, dated, and filed into that person's employee file — hiring, clearances, TB, training — and "TO COMPLETE" documents become tracked to-do items.
        Existing people are matched by employee number or name; missing people get a staff record. Duplicates are skipped.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label className={`inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-300 px-3 text-sm font-medium text-stone-600 transition-colors hover:border-primary/50 ${busy ? "opacity-50" : "cursor-pointer"}`} data-testid="pack-choose">
          <FileArchive size={15} /> {packFile ? packFile.name : "Choose the big ZIP"}
          <input type="file" className="hidden" disabled={busy} onChange={(e) => {
            const f = e.target.files?.[0] || null;
            if (f && !/\.(zip|pdf)$/i.test(f.name)) { toast.error("Choose a .zip pack or a combined .pdf"); e.target.value = ""; return; }
            setPackFile(f); setManifest(null); setResult(null);
          }} data-testid="pack-input" />
        </label>
        <Button onClick={uploadAndPreview} disabled={busy || !packFile} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="pack-preview">
          {progress ? `Uploading ${progress.done}/${progress.total}…` : busy ? "Reading…" : "Preview the import"}
        </Button>
        {manifest && (
          <Button onClick={runImport} disabled={busy} className="rounded-full bg-[#27AE60] text-white hover:bg-[#1e8449]" data-testid="pack-run">
            {busy ? "Importing…" : "Looks right — import now"}
          </Button>
        )}
      </div>

      {manifest && (
        <div className="mt-4 space-y-2" data-testid="pack-manifest">
          <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Preview — nothing written yet</p>
          {manifest.employees.map((e, i) => (
            <div key={i} className="rounded-lg border border-stone-200 p-4" data-testid={`pack-emp-${i}`}>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-stone-900">{e.name}{e.employee_id ? ` (#${e.employee_id})` : ""}</p>
                {e.match ? (
                  <span className="rounded-full bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-bold text-[#1e8449]">matches {e.match.name}</span>
                ) : (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">NEW — staff record will be created</span>
                )}
              </div>
              <p className="mt-1 text-xs text-stone-500">
                {e.documents} documents ({Object.entries(e.by_section).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ")})
                {e.to_complete > 0 && <> · <b className="text-[#8a5d14]">{e.to_complete} to-complete item(s)</b></>}
              </p>
            </div>
          ))}
        </div>
      )}

      {result && (
        <div className="mt-4 space-y-1.5" data-testid="pack-result">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#1e8449]">Imported</p>
          {result.results.map((r, i) => (
            <p key={i} className="text-xs text-stone-600" data-testid={`pack-result-${i}`}>
              <b>{r.name}</b>{r.created_user ? " (new staff record)" : ""}: {r.imported} documents filed
              {r.skipped_existing ? `, ${r.skipped_existing} already on file` : ""}{r.tasks_created ? `, ${r.tasks_created} to-complete opened` : ""}
            </p>
          ))}
        </div>
      )}
      <div className="mt-4 flex items-center gap-3 border-t border-stone-100 pt-3" data-testid="backfill-dates-row">
        <Button variant="outline" onClick={backfillDates} disabled={!!dateJob && !dateJob.finished}
          className="rounded-full border-primary/40 text-primary hover:bg-primary hover:text-white" data-testid="backfill-dates-btn">
          Fill in dates & record fields from documents (OCR)
        </Button>
        {dateJob && !dateJob.finished && (
          <span className="text-xs text-stone-500" data-testid="backfill-dates-progress">
            Reading documents… {dateJob.done} processed ({dateJob.filled} dates found{dateJob.remaining != null ? `, ${dateJob.remaining} left` : ""})
          </span>
        )}
        {dateJob?.finished && (
          <span className="text-xs font-medium text-[#1e8449]" data-testid="backfill-dates-done">
            Filled {dateJob.filled} document date(s) · updated {dateJob.profiles || 0} employee record(s)
          </span>
        )}
      </div>
    </div>
  );
}

export default function SetupUploadsPage() {
  const [users, setUsers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [cat, setCat] = useState("doc");
  const [empId, setEmpId] = useState("");
  const [empQuery, setEmpQuery] = useState("");
  const [docType, setDocType] = useState("id_card");
  const [recKind, setRecKind] = useState("competency_exam");
  const [topicId, setTopicId] = useState("");
  const [date, setDate] = useState("");
  const [evvMonth, setEvvMonth] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [bulkData, setBulkData] = useState(null);
  const [history, setHistory] = useState([]);
  const [queue, setQueue] = useState([]);
  const [qAssign, setQAssign] = useState({});
  const [qAlias, setQAlias] = useState({});

  const resolveQueueItem = async (qid) => {
    const uid = qAssign[qid];
    if (!uid) { toast.error("Pick the employee first"); return; }
    try {
      await api.post(`/import-queue/${qid}/resolve`, { user_id: uid, save_alias: !!qAlias[qid] });
      toast.success("Attached to employee");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resolveQueueName = async (nm, count) => {
    const sel = qAssign[nm];
    if (!sel) { toast.error("Pick the employee first"); return; }
    try {
      const body = sel === "__create__"
        ? { name_guess: nm, create_record: true }
        : { name_guess: nm, user_id: sel, save_alias: !!qAlias[nm] };
      const { data } = await api.post("/import-queue/assign-name", body);
      toast.success(`${data.assigned} item(s) attached to ${data.assigned_to}${data.failed ? ` (${data.failed} skipped — already on file)` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const dismissQueueName = async (nm) => {
    const items = queue.filter((x) => (x.name_guess || "?") === nm);
    if (!window.confirm(`Dismiss all ${items.length} item(s) for ${nm}? This cannot be undone.`)) return;
    for (const q of items) { await api.post(`/import-queue/${q.id}/dismiss`).catch(() => {}); }
    load();
  };

  const dismissQueueItem = async (qid) => {
    try {
      await api.post(`/import-queue/${qid}/dismiss`);
      setQueue((cur) => cur.filter((q) => q.id !== qid));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const ref = useRef(null);
  const uploaderRef = useRef(null);
  const shortcutTo = (c) => {
    setCat(c);
    setResult(null);
    setEvResult(null);
    setVisitResult(null);
    setTimeout(() => uploaderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  // Visit log special flow (chunked upload + dry-run preview)
  const [visitFile, setVisitFile] = useState(null);
  const [visitProgress, setVisitProgress] = useState(null);
  const [visitResult, setVisitResult] = useState(null);
  const [dryRun, setDryRun] = useState(true);

  // Evidence ZIP special flow (dupe reasons + unmatched-name picker)
  const [evZipFile, setEvZipFile] = useState(null);
  const [evResult, setEvResult] = useState(null);
  const [evSuggestions, setEvSuggestions] = useState(null);
  const [evGroups, setEvGroups] = useState([]);
  const [evAssign, setEvAssign] = useState({});
  const [evAlias, setEvAlias] = useState({});

  const load = () => {
    api.get("/users").then((r) => setUsers(Array.isArray(r.data) ? r.data : r.data.users || [])).catch(() => {});
    api.get("/training/topics").then((r) => setTopics(Array.isArray(r.data) ? r.data : r.data.topics || [])).catch(() => {});
    api.get("/uploads/history").then((r) => setHistory(r.data.history || [])).catch(() => {});
    api.get("/import-queue").then((r) => setQueue(r.data.items || [])).catch(() => {});
  };
  useEffect(load, []);

  const category = CATEGORIES.find((c) => c.key === cat);
  const isSingle = SINGLE_KEYS.includes(cat);
  const staff = useMemo(() => users.filter((u) => u.active !== false && (u.role || "staff") !== "admin")    .sort((a, b) => (a.name || "").localeCompare(b.name || "")), [users]);
  const pickerPeople = useMemo(() => users
    .filter((u) => (u.active !== false && (u.role || "staff") !== "admin") || u.profile?.history_record || u.role === "admin")
    .sort((a, b) => (a.name || "").localeCompare(b.name || "")), [users]);
  const filteredStaff = useMemo(() => {
    const q = empQuery.trim().toLowerCase();
    return q ? staff.filter((u) => (u.name || "").toLowerCase().includes(q)) : staff;
  }, [staff, empQuery]);
  const pickedEmp = staff.find((u) => u.id === empId);

  const canUpload = !isSingle || (empId && (cat !== "ev" || topicId));

  const uploadVisits = async () => {
    if (!visitFile) { toast.error("Choose the visit log file first"); return; }
    setBusy(true);
    setVisitResult(null);
    setVisitProgress({ phase: "uploading", done: 0, total: 1 });
    try {
      const CHUNK = 4 * 1024 * 1024;
      const { data: start } = await api.post("/visits/import/start", {});
      const total = Math.max(1, Math.ceil(visitFile.size / CHUNK));
      for (let i = 0; i < total; i++) {
        const blob = visitFile.slice(i * CHUNK, (i + 1) * CHUNK);
        await api.post(`/visits/import/chunk/${start.upload_id}`, blob, {
          headers: { "Content-Type": "application/octet-stream" }, timeout: 120000,
        });
        setVisitProgress({ phase: "uploading", done: i + 1, total });
      }
      setVisitProgress({ phase: "processing", done: total, total });
      const { data } = await api.post("/visits/import/finish",
        { upload_id: start.upload_id, filename: visitFile.name, dry_run: dryRun }, { timeout: 600000 });
      setVisitResult(data);
      toast.success(data.dry_run
        ? `Preview: ${data.rows} rows recognized — nothing written yet`
        : `Imported ${data.inserted} visits (${data.duplicates} duplicates skipped)`);
      setVisitFile(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setVisitProgress(null);
    }
  };

  const doEvidenceImport = async (file) => {
    setBusy(true);
    setEvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `/training-evidence/bulk-import${topicId ? `?topic_id=${topicId}` : ""}`;
      const { data } = await api.post(url, fd, { timeout: 300000 });
      setEvResult(data);
      toast.success(`Imported ${data.imported} evidence files (${data.skipped_duplicates} duplicates skipped)`);
      if (data.unmatched_names?.length) {
        const { data: sug } = await api.post("/training-evidence/suggest-matches", { names: data.unmatched_names });
        setEvSuggestions(sug.suggestions || []);
        const groups = {};
        for (const fn of data.unmatched_names) {
          const s = (sug.suggestions || []).find((x) => x.filename === fn);
          const key = s?.name_guess || fn;
          (groups[key] = groups[key] || { name: key, files: [], candidates: s?.candidates || [] }).files.push(fn);
        }
        setEvGroups(Object.values(groups));
        setEvAssign({});
        setEvAlias({});
      } else {
        setEvSuggestions(null);
        setEvGroups([]);
        setEvZipFile(null);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const attachAssigned = async () => {
    const entries = Object.entries(evAssign).filter(([, uid]) => uid);
    if (!entries.length || !evZipFile) { toast.error("Pick a person for at least one unmatched name"); return; }
    setBusy(true);
    try {
      const assignments = {};
      for (const [gname, uid] of entries) {
        const g = evGroups.find((x) => x.name === gname);
        if (!g) continue;
        if (evAlias[gname]) {
          const { data: det } = await api.get(`/users/${uid}`);
          const cur = det.profile?.aliases || [];
          if (!cur.includes(gname)) await api.put(`/users/${uid}/profile`, { aliases: [...cur, gname] });
        }
        for (const fn of g.files) assignments[fn] = uid;
      }
      const fd = new FormData();
      fd.append("file", evZipFile);
      fd.append("assignments", JSON.stringify(assignments));
      const { data } = await api.post("/training-evidence/bulk-import", fd, { timeout: 300000 });
      toast.success(`Attached ${data.imported} file(s) to assigned staff`);
      setEvResult(data);
      setEvSuggestions(null);
      setEvGroups([]);
      setEvZipFile(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    if (isSingle && !empId) { toast.error("Pick the employee first"); return; }
    if (cat === "ev" && !topicId) { toast.error("Pick the training topic first"); return; }
    if (category.special === "visits") { uploadVisits(); return; }
    if (category.special === "evzip") { doEvidenceImport(files[0]); return; }
    setBusy(true);
    setResult(null);
    setBulkData(null);
    try {
      const fd = new FormData();
      let url = category.url;
      if (cat === "doc") { url = `/users/${empId}/documents/${docType}`; fd.append("file", files[0]); if (date) fd.append("completed_date", date); }
      else if (cat === "rec") { url = `/users/${empId}/employment-records`; fd.append("file", files[0]); fd.append("kind", recKind); if (date) fd.append("record_date", date); }
      else if (cat === "ev") { url = `/users/${empId}/training-evidence`; fd.append("file", files[0]); fd.append("topic_id", topicId); if (date) fd.append("form_date", date); }
      else if (cat === "tb") { url = `/tb-screen/upload${date ? `?screen_date=${date}&` : "?"}user_id=${empId}`; fd.append("files", files[0]); }
      else if (category.multiple) {
        // batched: max 10 files / ~14MB per request so Cloudflare/proxy limits can't kill the whole upload
        const batches = []; let cur = []; let sz = 0;
        for (const f of files) {
          if (cur.length && (cur.length >= 10 || sz + f.size > 14 * 1024 * 1024)) { batches.push(cur); cur = []; sz = 0; }
          cur.push(f); sz += f.size;
        }
        if (cur.length) batches.push(cur);
        let merged = null; const failedBatches = [];
        for (let bi = 0; bi < batches.length; bi++) {
          if (batches.length > 1) setResult(`Uploading batch ${bi + 1} of ${batches.length}…`);
          const bfd = new FormData();
          for (const f of batches[bi]) bfd.append("files", f);
          try {
            const { data: bd } = await api.post(url, bfd, { timeout: 300000 });
            merged = merged || {};
            for (const [k, v] of Object.entries(bd || {})) {
              if (typeof v === "number") merged[k] = (merged[k] || 0) + v;
              else if (Array.isArray(v)) merged[k] = [...(merged[k] || []), ...v];
              else if (merged[k] === undefined) merged[k] = v;
            }
          } catch (be) {
            failedBatches.push(bi + 1);
            await api.post("/admin/upload-log", { endpoint: url, filenames: batches[bi].map((f) => f.name), detail: `client-side: ${be?.message || be}` }).catch(() => {});
          }
        }
        if (failedBatches.length) toast.error(`Batch(es) ${failedBatches.join(", ")} failed (network) — details in the upload log below; re-upload just those files`);
        setResult(`${category.fmt(merged || {})}${failedBatches.length ? ` · ${failedBatches.length} batch(es) FAILED — see upload log` : ""}`);
        setBulkData(merged);
        if (!failedBatches.length) toast.success("Upload complete");
        load();
        window.dispatchEvent(new Event("uploads-changed"));
      }
      else {
        fd.append("file", files[0]);
        if (cat === "evv-month" && evvMonth) url = `${url}?month=${evvMonth}`;
        const { data } = await api.post(url, fd, { timeout: 300000 });
        setResult(isSingle ? `Uploaded for ${pickedEmp?.name || "employee"} ✓` : category.fmt(data));
        setBulkData(data);
        toast.success("Upload complete");
        load();
        window.dispatchEvent(new Event("uploads-changed"));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const selCls = "h-10 w-full rounded-md border border-stone-300 bg-white px-3 text-sm";

  return (
    <div className="max-w-4xl space-y-6" data-testid="setup-uploads-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="setup-uploads-title">Uploads</h1>
        <p className="mt-1 max-w-2xl text-sm text-stone-500">Every upload in the app lives here — a single file for one person, or a whole ZIP/CSV at once.</p>
      </div>

      {/* Instructions */}
      <div className="rounded-xl border border-stone-200 bg-accent/40 p-5 text-sm leading-relaxed text-stone-600" data-testid="upload-instructions">
        <p className="font-heading text-sm font-semibold text-stone-800">How this works</p>
        <ol className="mt-2 list-decimal space-y-1.5 pl-5">
          <li><strong>Pick what you're uploading</strong> — a single document for one employee, or a bulk ZIP/CSV for many at once.</li>
          <li><strong>Single files:</strong> pick the employee, the exact type, and the <em>date on the document</em> (not today's date). It lands on their record instantly.</li>
          <li><strong>Bulk ZIPs:</strong> don't rename the files — employee names are read from the filenames automatically. Anything unmatched is listed afterward with a one-click way to assign it.</li>
          <li><strong>Visit log:</strong> runs as a preview first — nothing is written until you uncheck "Preview only". Duplicates are skipped by Visit ID, so re-runs are safe.</li>
        </ol>
      </div>

      <RecurringDataCard onShortcut={shortcutTo} />

      <EmployeePackImport />

      {/* Unified uploader */}
      <div ref={uploaderRef} className="scroll-mt-24 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="unified-uploader">
        <p className="font-heading text-sm font-semibold text-stone-900">Upload</p>
        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">1 · What are you uploading?</label>
            <select value={cat} onChange={(e) => { setCat(e.target.value); setResult(null); setEvResult(null); setVisitResult(null); }} className={selCls} data-testid="upload-category">
              <optgroup label="Single employee">
                {CATEGORIES.filter((c) => c.group === "Single employee").map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </optgroup>
              <optgroup label="Bulk (many records at once)">
                {CATEGORIES.filter((c) => c.group === "Bulk").map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </optgroup>
            </select>
          </div>

          {isSingle && (
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">2 · Which employee?</label>
              <input value={empQuery} onChange={(e) => { setEmpQuery(e.target.value); setEmpId(""); }}
                placeholder="Start typing a name…" className="h-10 w-full rounded-md border border-stone-300 bg-white px-3 text-sm"
                list="upload-employee-list" data-testid="upload-employee" />
              <datalist id="upload-employee-list">
                {filteredStaff.map((u) => <option key={u.id} value={u.name} />)}
              </datalist>
              {empQuery && !empId && filteredStaff.length > 0 && (
                <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-stone-200 bg-white shadow-sm" data-testid="upload-employee-options">
                  {filteredStaff.slice(0, 8).map((u) => (
                    <button key={u.id} type="button" onClick={() => { setEmpId(u.id); setEmpQuery(u.name || ""); }}
                      className="block w-full px-3 py-2 text-left text-sm text-stone-700 hover:bg-accent" data-testid={`upload-emp-${u.id}`}>
                      {u.name}
                    </button>
                  ))}
                </div>
              )}
              {pickedEmp && <p className="mt-1 text-xs font-medium text-primary" data-testid="upload-employee-picked">✓ {pickedEmp.name}</p>}
            </div>
          )}

          {cat === "doc" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Document type</label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)} className={selCls} data-testid="upload-doctype">
                {DOC_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          )}
          {cat === "rec" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Record type</label>
              <select value={recKind} onChange={(e) => setRecKind(e.target.value)} className={selCls} data-testid="upload-reckind">
                {RECORD_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          )}
          {(cat === "ev" || cat === "ev-zip") && (
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Training topic{cat === "ev-zip" ? " (optional — otherwise matched from filename)" : ""}</label>
              <select value={topicId} onChange={(e) => setTopicId(e.target.value)} className={selCls} data-testid="upload-topic">
                <option value="">{cat === "ev-zip" ? "Match from filenames" : "Pick a topic…"}</option>
                {topics.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </div>
          )}
          {isSingle && (
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Date on the document (optional but recommended)</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="upload-date" />
            </div>
          )}
          {cat === "evv-month" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Which month?</label>
              <input type="month" value={evvMonth} onChange={(e) => setEvvMonth(e.target.value)} className="h-10 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="upload-evv-month" />
            </div>
          )}

          {category.special === "visits" ? (
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex h-10 cursor-pointer items-center rounded-md border border-stone-300 px-3 text-sm font-medium text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="visit-upload-label">
                  {visitFile ? visitFile.name : "Choose visit log file"}
                  <input type="file" accept=".xlsx,.xls,.csv,.tsv,.txt" className="hidden" data-testid="visit-upload-input"
                    onChange={(e) => setVisitFile(e.target.files?.[0] || null)} />
                </label>
                <Button onClick={uploadVisits} disabled={busy || !visitFile} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="visit-upload-button">
                  {busy ? (dryRun ? "Previewing…" : "Importing…") : dryRun ? "3 · Preview import" : "3 · Import visits"}
                </Button>
                <label className="flex items-center gap-1.5 text-xs text-stone-500">
                  <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="visit-dry-run-toggle" />
                  Preview only — check matches before writing
                </label>
              </div>
              {visitProgress && (
                <p className="mt-3 text-xs font-medium text-stone-500" data-testid="visit-upload-progress">
                  {visitProgress.phase === "uploading"
                    ? `Uploading… part ${visitProgress.done} of ${visitProgress.total}`
                    : "Upload complete — reading rows now. Large files can take a few minutes; leave this tab open."}
                </p>
              )}
              {visitResult && (
                <div className={`mt-3 rounded-md p-3 text-xs ${visitResult.dry_run ? "border border-[#B7791F]/40 bg-[#B7791F]/5 text-stone-700" : "bg-stone-50 text-stone-600"}`} data-testid="visit-upload-result">
                  {visitResult.dry_run ? (
                    <p data-testid="visit-dry-run-summary"><strong>Preview only — nothing written.</strong> {visitResult.rows} rows recognized · dates {visitResult.date_first} → {visitResult.date_last} · {visitResult.matched_staff} matched to staff · {visitResult.matched_clients} matched to clients{visitResult.missed_visits > 0 ? ` · ${visitResult.missed_visits} missed visits` : ""}. Review the unmatched names below, then uncheck &ldquo;Preview only&rdquo; and import again to write.</p>
                  ) : (
                    <p><strong>{visitResult.inserted}</strong> visits imported · {visitResult.duplicates} duplicates skipped · {visitResult.matched_staff} rows matched to staff · {visitResult.matched_clients} matched to clients{visitResult.missed_visits > 0 ? ` · ${visitResult.missed_visits} missed visits` : ""}</p>
                  )}
                  {visitResult.unmatched_staff?.length > 0 && (
                    <p className="mt-1 text-[#8a5d14]"><strong>Unmatched caregiver names:</strong> {visitResult.unmatched_staff.join(", ")}</p>
                  )}
                  {visitResult.client_stubs?.length > 0 && (
                    <p className="mt-1 text-[#8a5d14]" data-testid="visit-client-stubs"><strong>{visitResult.dry_run ? "New client stubs to create" : "Client stubs created"} ({visitResult.client_stubs.length}):</strong> {visitResult.client_stubs.slice(0, 20).join(", ")}{visitResult.client_stubs.length > 20 ? "…" : ""}</p>
                  )}
                  {visitResult.aliases_learned?.length > 0 && (
                    <p className="mt-1 text-[#1e8449]" data-testid="visit-aliases-learned"><strong>Name aliases {visitResult.dry_run ? "that will be saved" : "saved"} ({visitResult.aliases_learned.reduce((n, a) => n + a.aliases.length, 0)}):</strong> {visitResult.aliases_learned.map((a) => `${a.aliases.join(" / ")} → ${a.name}`).join(" · ")} — future imports match these names automatically.</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              <input ref={ref} type="file" accept={category.accept} multiple={!!category.multiple} className="hidden"
                onChange={(e) => {
                  if (category.special === "evzip") setEvZipFile(e.target.files?.[0] || null);
                  upload(e.target.files);
                  e.target.value = "";
                }} data-testid="upload-file-input" />
              <Button onClick={() => ref.current?.click()} disabled={busy || !canUpload}
                className="rounded-full bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-stone-900" data-testid="upload-submit">
                <UploadSimple size={15} className="mr-1.5" /> {busy ? "Uploading…" : "3 · Choose file & upload"}
              </Button>
              {!canUpload && <p className="mt-1 text-[11px] text-stone-400">Pick an employee{cat === "ev" ? " and topic" : ""} first.</p>}
            </div>
          )}
          {result && <p className="rounded-md bg-accent/50 px-3 py-2 text-xs text-stone-600" data-testid="upload-result">{result}</p>}
          {bulkData?.credentials?.length > 0 && (
            <div className="rounded-md border border-[#B7791F]/40 bg-[#B7791F]/5 p-3 text-xs" data-testid="upload-credentials">
              <p className="font-semibold text-[#8a5d14]">Temporary passwords — copy these now; they won't be shown again:</p>
              <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-stone-200 bg-white p-2 font-mono text-stone-700">
                {bulkData.credentials.map((c) => <p key={c.email}>{c.email} — {c.temp_password}</p>)}
              </div>
            </div>
          )}

          {evResult && (
            <div className="rounded-md bg-accent/40 p-3 text-xs text-stone-600" data-testid="evidence-zip-result">
              <p><strong>{evResult.imported}</strong> files attached · {evResult.skipped_duplicates} duplicates skipped · {evResult.unmatched_name_count} unmatched names{evResult.no_date_count ? ` · ${evResult.no_date_count} without a completion date` : ""}</p>
              {evResult.dupe_details?.length > 0 && (
                <div className="mt-2 rounded-md border border-stone-200 bg-white p-2.5" data-testid="evidence-dupe-details">
                  <p className="font-semibold text-stone-700">Why the {evResult.skipped_duplicates} duplicate(s) were skipped — a different completion date is never blocked:</p>
                  <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto text-stone-500">
                    {evResult.dupe_details.map((d, i) => (
                      <li key={i} data-testid={`ev-dupe-${i}`}><span className="font-medium text-stone-600">{d.filename}</span> — {d.reason}{d.matched_to ? ` (person: ${d.matched_to})` : ""}</li>
                    ))}
                  </ul>
                </div>
              )}
              {evResult.no_date_files?.length > 0 && (
                <p className="mt-1 text-[#8a5d14]"><strong>No completion date found (not attached):</strong> {evResult.no_date_files.slice(0, 20).join(", ")}{evResult.no_date_files.length > 20 ? "…" : ""}</p>
              )}
              {evResult.unmatched_names?.length > 0 && (
                <div className="mt-2" data-testid="evidence-unmatched-panel">
                  <p className="font-semibold text-[#8a5d14]">Unmatched names — assign each person once; it applies to all their files:</p>
                  <div className="mt-1.5 space-y-1.5">
                    {(evGroups || []).map((g) => (
                      <div key={g.name} className="flex flex-wrap items-center gap-2 rounded-md border border-stone-200 bg-white px-2.5 py-1.5" data-testid={`ev-unmatched-${g.name.replace(/\W+/g, "-").slice(0, 24)}`}>
                        <span className="min-w-40 flex-1 text-stone-700"><span className="font-medium">{g.name}</span> <span className="text-stone-400">({g.files.length} file{g.files.length === 1 ? "" : "s"})</span></span>
                        {(g.candidates || []).map((c) => (
                          <button key={c.user_id} type="button" onClick={() => setEvAssign((a) => ({ ...a, [g.name]: c.user_id }))}
                            className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${evAssign[g.name] === c.user_id ? "border-primary bg-primary/10 text-primary" : "border-stone-300 text-stone-600 hover:border-primary/50"}`}
                            data-testid={`ev-suggest-${g.name.replace(/\W+/g, "-").slice(0, 16)}-${c.user_id}`}>{c.name}?</button>
                        ))}
                        <select value={evAssign[g.name] || ""} onChange={(e) => setEvAssign((a) => ({ ...a, [g.name]: e.target.value }))}
                          className="h-7 max-w-44 rounded-md border border-stone-300 px-1 text-[11px]" data-testid={`ev-assign-select-${g.name.replace(/\W+/g, "-").slice(0, 16)}`}>
                          <option value="">Choose person…</option>
                          {(pickerPeople || []).map((s) => <option key={s.id} value={s.id}>{s.name}{s.profile?.history_record ? " (history record)" : s.role === "admin" ? " (office)" : ""}</option>)}
                        </select>
                        <label className="flex items-center gap-1 text-[11px] text-stone-500" data-testid={`ev-alias-label-${g.name.replace(/\W+/g, "-").slice(0, 16)}`}>
                          <input type="checkbox" checked={!!evAlias[g.name]} onChange={(e) => setEvAlias((a) => ({ ...a, [g.name]: e.target.checked }))} data-testid={`ev-alias-${g.name.replace(/\W+/g, "-").slice(0, 16)}`} />
                          save as alias
                        </label>
                      </div>
                    ))}
                  </div>
                  <Button onClick={attachAssigned} disabled={busy} size="sm" className="mt-2 bg-primary text-primary-foreground" data-testid="ev-attach-assigned">
                    {busy ? "Attaching…" : "Attach assigned files"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <DocumentInboxCard staff={pickerPeople} />
      <BulkBadgePhotos staff={staff} />

      <UntopicedEvidenceCard />

      {queue.length > 0 && (
        <div className="rounded-xl border border-[#B7791F]/30 bg-white p-5 shadow-sm" data-testid="review-queue-card">
          <p className="font-heading text-sm font-semibold text-stone-900">Needs review — unmatched uploads ({queue.length})</p>
          <p className="mt-0.5 text-xs text-stone-400">Grouped by name — assign each NAME once and every item for them attaches. "Create history record" keeps a former worker's training history on file without giving them a login. Tick "save as alias" so the name matches automatically next time.</p>
          <div className="mt-3 space-y-2">
            {(() => {
              const groups = {};
              for (const q of queue) { (groups[q.name_guess || "?"] = groups[q.name_guess || "?"] || []).push(q); }
              return Object.keys(groups).sort((a, b) => groups[b].length - groups[a].length).map((nm) => (
                <div key={nm} className="rounded-md border border-stone-200" data-testid={`queue-group-${nm}`}>
                  <div className="flex flex-wrap items-center gap-2 px-2.5 py-2">
                    <button onClick={() => setQAssign((a) => ({ ...a, [`__open_${nm}`]: !a[`__open_${nm}`] }))}
                      className="min-w-40 flex-1 text-left text-xs" data-testid={`queue-group-toggle-${nm}`}>
                      <span className="font-semibold text-stone-800">{nm}</span>
                      <span className="text-stone-400"> · {groups[nm].length} item{groups[nm].length === 1 ? "" : "s"} — click to {qAssign[`__open_${nm}`] ? "hide" : "see"} them</span>
                    </button>
                    <select value={qAssign[nm] || ""} onChange={(e) => setQAssign((a) => ({ ...a, [nm]: e.target.value }))}
                      className="h-8 max-w-52 rounded-md border border-stone-300 px-1.5 text-[11px]" data-testid={`queue-assign-name-${nm}`}>
                      <option value="">Choose person…</option>
                      <option value="__create__">＋ Create history record (former staff — no login)</option>
                      {(pickerPeople || []).map((s) => <option key={s.id} value={s.id}>{s.name}{s.profile?.history_record ? " (history record)" : s.role === "admin" ? " (office)" : ""}</option>)}
                    </select>
                    <label className="flex items-center gap-1 text-[11px] text-stone-500" data-testid={`queue-alias-name-label-${nm}`}>
                      <input type="checkbox" checked={!!qAlias[nm]} onChange={(e) => setQAlias((a) => ({ ...a, [nm]: e.target.checked }))} data-testid={`queue-alias-name-${nm}`} />
                      save as alias
                    </label>
                    <Button size="sm" onClick={() => resolveQueueName(nm, groups[nm].length)} className="bg-primary text-primary-foreground" data-testid={`queue-resolve-name-${nm}`}>
                      Attach all {groups[nm].length}
                    </Button>
                    <button onClick={() => dismissQueueName(nm)} className="text-stone-300 hover:text-[#C0392B]" title={`Dismiss all ${nm} items`} data-testid={`queue-dismiss-name-${nm}`}>✕</button>
                  </div>
                  {qAssign[`__open_${nm}`] && (
                    <div className="space-y-1 border-t border-stone-100 px-2.5 py-2" data-testid={`queue-group-rows-${nm}`}>
                      {groups[nm].map((q) => (
                        <div key={q.id} className="flex flex-wrap items-center gap-2 text-[11px] text-stone-600" data-testid={`queue-item-${q.id}`}>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${q.kind === "evidence" ? "bg-primary/10 text-primary" : "bg-[#27AE60]/10 text-[#1e8449]"}`}>
                            {q.kind === "evidence" ? "PROOF" : "COMPLETION"}
                          </span>
                          <span className="flex-1">{q.topic_guess || q.filename || ""}{q.form_date ? ` · ${q.form_date}` : ""}</span>
                          <button onClick={() => dismissQueueItem(q.id)} className="text-stone-300 hover:text-[#C0392B]" data-testid={`queue-dismiss-${q.id}`}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="upload-history">
          <p className="font-heading text-sm font-semibold text-stone-900">Recent uploads</p>
          <p className="mt-0.5 text-xs text-stone-400">Who uploaded what, most recent first — across the whole app.</p>
          <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto text-xs text-stone-600">
            {history.map((h, i) => (
              <li key={i} className="flex flex-wrap gap-2 border-b border-stone-50 pb-1" data-testid={`upload-history-${i}`}>
                <span className="w-32 shrink-0 text-stone-400">{String(h.at || "").slice(0, 16).replace("T", " ")}</span>
                <span className="font-medium text-stone-700">{h.who}</span>
                <span className="flex-1">{h.action}{h.detail ? ` — ${h.detail}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ClearTrainingCard />
      <UploadLogCard />
    </div>
  );
}
